import { makeAutoObservable, runInAction } from "mobx";
import { OptionalScope } from "../../shared/models/OptionalScope";
import { RootStore } from "../../shared/stores/RootStore";
import { LifeCycleStage } from "../models/LifeCycleStage";
import { CraWayApi } from "../services/CraWayApi";
import { CreateCraWay, CraWay, UpdateCraWay } from "../models/CraWay";
import { SurveyApi } from "../../survey/services/SurveyApi";

export const defaultCraWayScopes = [
  OptionalScope.Stats,
  OptionalScope.GeoJson,
  OptionalScope.Description,
  OptionalScope.PlannedTrajectory,
  OptionalScope.ScoreImpact,
  OptionalScope.Score,
  OptionalScope.SafetyTreatmentModel,
];

export class CraWayStore {
  rootStore;
  ways: CraWay[] = [];
  selectedId?: string;

  pageSize = 4;
  nextPage: number | null = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.ways = [];
      this.selectedId = undefined;
      this.nextPage = 0;
    });
  }

  get(craWayId: string): CraWay | undefined {
    const { filterStore } = this.rootStore;
    const ways = filterStore.originalOrForecastedCraWays;
    return ways.find((way) => way.id === craWayId);
  }

  getSelected() {
    if (!this.selectedId) return;
    return this.get(this.selectedId);
  }

  select(craWayId: string) {
    const { hlaStore } = this.rootStore;
    hlaStore.clearSelected();

    runInAction(() => (this.selectedId = craWayId));
  }

  clearSelected() {
    const { craNodeStore } = this.rootStore;
    craNodeStore.clearSelected();
    runInAction(() => (this.selectedId = undefined));
  }

  async refreshCraWay(craWayId: string) {
    const { data: way } = await CraWayApi.get(craWayId, defaultCraWayScopes);
    runInAction(() => {
      this.ways = this.ways.map((r) => (r.id === craWayId ? way : r));
    });
  }

  async fetchAllWays(surveyId: string, cancelIf?: () => boolean) {
    runInAction(() => {
      this.ways = [];
      this.nextPage = 0;
    });
    while (this.nextPage !== null) {
      const response = await SurveyApi.pageCraWays(
        surveyId,
        defaultCraWayScopes,
        this.pageSize,
        this.nextPage,
      );
      if (cancelIf?.()) return;
      runInAction(() => {
        this.ways = this.ways.concat(response.data.data);
        this.nextPage = response.data.meta.nextPage;
      });
    }
  }

  async fetchAndSelectCraWay(craWayId: string) {
    const { data: way } = await CraWayApi.get(craWayId, defaultCraWayScopes);
    runInAction(() => {
      this.ways = [way];
      this.select(way.id);
    });
  }

  async deleteCraWay(id: string) {
    await CraWayApi.delete(id);
    runInAction(() => {
      this.ways = this.ways.filter((way) => way.id !== id);
    });
  }

  async createCraWay(request: CreateCraWay) {
    const { data: createdCraWay } = await CraWayApi.create(
      request,
      defaultCraWayScopes,
    );

    await this.fetchAllWays(createdCraWay.surveyId);
    this.select(createdCraWay.id);

    return createdCraWay;
  }

  async updateCraWay(
    craWayId: string,
    request: UpdateCraWay,
    scopes: OptionalScope[] = defaultCraWayScopes,
  ) {
    const { data: updatedWay } = await CraWayApi.update(
      craWayId,
      request,
      scopes,
    );
    runInAction(() => {
      this.ways = this.ways.map((way) =>
        way.id === craWayId ? updatedWay : way,
      );
    });
    return updatedWay;
  }

  async updateLifeCycleStage(craWayId: string, lifeCycleStage: LifeCycleStage) {
    await CraWayApi.updateLifeCycleStage(
      craWayId,
      lifeCycleStage,
      defaultCraWayScopes,
    ).then(({ data: updatedWay }) => {
      runInAction(() => {
        this.ways = this.ways.map((way) =>
          way.id === craWayId ? updatedWay : way,
        );
      });
    });
  }

  async compileCraWayNodes(craWayId: string) {
    const { data: updatedWay } = await CraWayApi.compileNodes(
      craWayId,
      defaultCraWayScopes,
    );

    runInAction(() => {
      this.ways = this.ways.map((way) =>
        way.id === craWayId ? updatedWay : way,
      );
    });
  }
}
